import { GridSearchBox } from '@progress/kendo-react-grid';
import { xCircleIcon } from '@progress/kendo-svg-icons';
import { Button } from '../Button';
import { getSelectedArray } from './helpers';

/**
 * TableToolbar component for Kendo UI Grid.
 *
 * @param {Object} params - Table props
 * @param {boolean} [params.exportable=false] - Whether the table is exportable.
 * @param {Function} params.onExport - Function to call when exporting.
 * @param {boolean} [params.showSearchbox=false] - Whether to use a search box.
 * @param {Function} params.onSearchChange - Function to call when search value changes.
 * @param {boolean} [params.filterable=false] - Whether the table is filterable.
 * @param {boolean} [params.groupable=false] - Whether the table is groupable.
 * @param {boolean} [params.sortable=false] - Whether the table is sortable.
 * @param {boolean} [params.selectable=false] - Whether the table is selectable.
 * @param {Object} [params.select={}] - Current selection state.
 * @param {Function} params.onSaveSelection - Function to call when saving selection.
 * @param {boolean} [params.showSaveSelectionButton=true] - Whether to show the Save Selection button.
 * @param {Object} [params.defaultSelect={}] - Default selection state for comparison.
 * @param {Function} params.onClearAllFilters - Function to call when clearing all filters.
 * @param {Object|null} [params.filter=null] - Current Kendo filter object structured from tableDataFilters.js (see Kendo UI DataSource filter format: https://www.telerik.com/kendo-jquery-ui/documentation/api/javascript/data/datasource/configuration/filter).
 * @param {string} [params.searchValue=''] - Current search value.
 * @param {Array} [params.group=[]] - Current group state.
 * @param {Array} [params.sort=[]] - Current sort state.
 * @param {Array} [params.data=[]] - The data for the table.
 * @param {string} [params.dataItemKey=''] - The unique data item key for selection.
 * @param {number} params.pageSize - Current page size.
 * @param {Object} params.dataState - Current data state.
 * @param {boolean} [params.showSaveSelectionButton=false] - Whether to show the Save Selection button.
 * @returns {JSX.Element} rendered toolbar component.
 */
export const TableToolbar = ({
    exportable = false,
    onExport,
    showSearchbox = false,
    searchValue = '',
    onSearchChange,
    filterable = false,
    groupable = false,
    sortable = false,
    selectable = false,
    onClearAllFilters,
    filter = null,
    select = {},
    defaultSelect = {},
    onSaveSelection,
    group = [],
    sort = [],
    data = [],
    dataItemKey = '',
    pageSize,
    dataState,
    showSaveSelectionButton = false
}) => {
    const hasPageSizeChanged = dataState.take !== pageSize;
    const isFilterEmpty = !filter ||
        (typeof filter === 'object' &&
        filter.logic === 'and' &&
        Array.isArray(filter.filters) &&
        filter.filters.length === 0);

    const isClearFiltersButtonDisabled =
        isFilterEmpty &&
        !searchValue &&
        group.length === 0 &&
        sort.length === 0 &&
        !hasPageSizeChanged ||
        data.length === 0;
    
    const hasSelectedRows = Object.keys(select).length > 0;
    const hasUnsavedChanges = JSON.stringify(select) !== JSON.stringify(defaultSelect);

    const handleSaveSelection = () => {  
        if (dataItemKey) {  
            const selectedArray = getSelectedArray(data, select, dataItemKey);  
            if (selectedArray.length !== 0) {  
                onSaveSelection(selectedArray);  
                return;  
            }  
        }  
        onSaveSelection([]);  
    };  

    return (
        <>
            {exportable && (
                <Button onClick={onExport} content='Export to Excel' themeColor='info' />
            )}
            {showSearchbox && (
                <GridSearchBox
                    value={searchValue}
                    onChange={onSearchChange}
                    placeholder="Search Report..."
                    className="k-textbox k-input"
                />
            )}
            {(filterable || groupable || sortable) && (
                <Button
                    onClick={onClearAllFilters}
                    content="Clear Filters"
                    svgIcon={xCircleIcon}
                    disabled={isClearFiltersButtonDisabled}
                />
            )}
            {selectable && showSaveSelectionButton && (handleSaveSelection !== null) && (
                <Button
                    onClick={handleSaveSelection}
                    content="Save Selection"
                    disabled={!hasSelectedRows || !hasUnsavedChanges}
                />
            )}
        </>
    );
};